import { router } from '@inertiajs/react';
import { useEffect } from 'react';
import { route } from 'ziggy-js';

/**
 * The marketing site's call-to-action targets, one per intent. Every page uses
 * these with the matching `site.cta.*` label, so a label never points at two
 * different places.
 */
export const CONSULTATION_URL = 'https://cal.com/synex/battery-reg';

/**
 * The example passport's address in production: config('synex.live_passport_url')'s
 * default there (the site's own /passport-example), and what the printed code
 * (public/images/passport-qr.svg, scripts/passport-qr.mjs) encodes. Read the
 * address with livePassportUrl(), never this.
 */
export const LIVE_PASSPORT_URL = 'https://synexcloud.com/passport-example';

/**
 * "See a passport": the address config('synex.live_passport_url')
 * names (env SYNEX_LIVE_PASSPORT_URL), which app.blade.php hands every page
 * as `window.__SYNEX_LIVE_PASSPORT_URL__`; the default without it (or with
 * anything but an http(s) address). Every CTA, the Contact page's link and
 * the codes the vignettes draw read it here, so one value moves them all.
 * On the site's own origin it is a page of the site (SmartLink: an Inertia
 * link, ArrowRight), elsewhere a new tab (ArrowUpRight).
 */
export function livePassportUrl() {
    const configured = typeof window === 'undefined' ? null : window.__SYNEX_LIVE_PASSPORT_URL__;

    return typeof configured === 'string' && /^https?:\/\/\S+$/.test(configured) ? configured : LIVE_PASSPORT_URL;
}

/** Where a product's example passport opens: the example page on that product ('battery', 'textile' or 'furniture'). */
export const exampleHref = (product) => `${livePassportUrl()}?product=${product}`;

/** "Read the API docs": the public API documentation. */
export const API_DOCS_URL = 'https://docs.synexcloud.com';

/** "Book a demo": the contact form with the demo reason pre-selected. */
export const demoHref = () => route('outer.contact', { reason: 'demo' });

/**
 * A trailing-slash-free pathname for any href. Ziggy hands back absolute URLs,
 * so both sides of an active-link comparison are reduced to this first.
 */
export function pathnameOf(href) {
    if (typeof href !== 'string') return null;

    try {
        return new URL(href, 'http://localhost').pathname.replace(/\/+$/, '') || '/';
    } catch {
        return null;
    }
}

/** The pathname of the page being shown, or null outside a browser. */
export function currentPathname() {
    return typeof window === 'undefined' ? null : pathnameOf(window.location.pathname);
}

/** Whether `href` points at the page whose pathname is `current`. */
export function isCurrent(href, current) {
    const path = pathnameOf(href);

    return path !== null && path === current;
}

/**
 * Keeps an anchor a page writes in place (`history.replaceState`, no history
 * entry, Inertia's `history.state` kept) on its history entry when the
 * visitor moves on. Inertia (1.3) does not know of such an anchor: when a
 * visit starts it saves the scroll over the entry it leaves, from its own
 * page's address, without the anchor. So the address with the anchor is
 * noted as the visit begins (`before`) and put back once Inertia has written
 * (`start`, a task later, since Inertia defers its write on Chrome for iOS):
 * Back returns to the anchor, and the address can still be copied with it.
 *
 * It only puts it back on the entry it left: if the next page is already in
 * (a new path or query), it leaves it alone. Nothing happens at mount, so it
 * never touches the address before Inertia's first setPage.
 *
 * It lives here, with the address helpers every marketing page loads, not in
 * a file of its own: four pages use it, and as a module of its own it was a
 * chunk of its own on each of them (Task 10b-3).
 */
export function useKeepHash() {
    useEffect(() => {
        let leaving = null;
        const offBefore = router.on('before', () => {
            const { hash, href, pathname, search } = window.location;
            leaving = hash ? { href, page: pathname + search } : null;
        });
        const offStart = router.on('start', () => {
            const left = leaving;
            leaving = null;
            if (!left) return;
            setTimeout(() => {
                const { href, pathname, search } = window.location;
                if (pathname + search === left.page && href !== left.href) window.history.replaceState(window.history.state, '', left.href);
            });
        });

        return () => {
            offBefore();
            offStart();
        };
    }, []);
}
